import { GalleryProps } from "@/components/Gallery/GalleryProps.ts";
import { DotButton } from "@/components/Gallery/components/GalleryDots/GalleryDots.tsx";
import { GalleryImage } from "@/components/Gallery/components/GalleryImage/GalleryImage.tsx";
import {
  NextButton,
  PrevButton,
} from "@/components/Gallery/components/GalleryNavigation/GalleryNavigation.tsx";
import { GalleryThumbnail } from "@/components/Gallery/components/GalleryThumbnail/GalleryThumbnail.tsx";
import { useDotButton } from "@/components/Gallery/hooks/useDotButton.ts";
import { ChevronLeft } from "@/components/Icons/utility/ChevronLeft.tsx";
import { ChevronRight } from "@/components/Icons/utility/ChevronRight.tsx";
import useEmblaCarousel, { EmblaCarouselType } from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

const EmblaCarousel = (props: GalleryProps) => {
  const { slides, options, thumbnails } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [gallery, emblaMainApi] = useEmblaCarousel(options);
  const [galleryThumbnails, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
  });

  const { scrollSnaps, onDotButtonClick } = useDotButton(emblaMainApi, selectedIndex, setSelectedIndex);

  const scrollPrev = useCallback(() => emblaMainApi?.scrollPrev(), [emblaMainApi]);
  const scrollNext = useCallback(() => emblaMainApi?.scrollNext(), [emblaMainApi]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi],
  );

  const onSelect = useCallback(
    (emblaMainApi: EmblaCarouselType) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      setSelectedIndex(emblaMainApi.selectedScrollSnap());
      emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
    },
    [emblaThumbsApi, setSelectedIndex],
  );

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect(emblaMainApi);
    emblaMainApi.on("select", onSelect);
    emblaMainApi.on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);

  const handleRenderSlides = () => {
    if (slides.length < 1) return null;

    return (
      <div className="c-gallery__container">
        {slides?.map((slide, index) => (
          <div className="c-gallery__slide u-ratio u-ratio--4-3" key={index}>
            <figure className="u-ratio__inner">
              <GalleryImage url={slide.url} disabled={props.disabled} />
            </figure>
          </div>
        ))}
      </div>
    );
  };

  const handleRenderThumbnailSlides = () => {
    if (slides.length < 1) return null;

    return (
      <div className="c-gallery-thumbnails__container">
        {slides?.map((slide, index) => (
          <GalleryThumbnail
            onClick={() => onThumbClick(index)}
            selected={index === selectedIndex}
            imgSrc={slide.url}
            key={index}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="c-gallery">
      <div className="c-gallery__viewport" ref={gallery}>
        {handleRenderSlides()}
        <div className="c-gallery__buttons">
          <PrevButton onClick={scrollPrev}>
            <ChevronLeft />
          </PrevButton>
          <NextButton onClick={scrollNext}>
            <ChevronRight />
          </NextButton>
        </div>
        {thumbnails || (
          <div className="embla__controls">
            <div className="embla__dots">
              {scrollSnaps?.map((_, index) => (
                <DotButton
                  key={index}
                  onClick={() => onDotButtonClick(index)}
                  className={"embla__dot".concat(index === selectedIndex ? " embla__dot--selected" : "")}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {thumbnails && (
        <div className="c-gallery-thumbnails">
          <div className="c-gallery-thumbnails__viewport" ref={galleryThumbnails}>
            {handleRenderThumbnailSlides()}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmblaCarousel;
